/**
 * Engagement-driven proposal chase — governed moves from real telemetry.
 *
 * PandaDoc shows you view analytics and stops there. This module turns the
 * same signals (view counts, last-viewed, most-viewed section, time since
 * send) into concrete, APPROVAL-GATED chase moves:
 *
 *   • hot-but-stalling (viewed ≥ threshold, unsigned > 48h) → nudge email
 *     that cites the section the buyer keeps re-reading;
 *   • sent-but-cold (never viewed past `staleHours`) → risk flag so the AE
 *     hears about a dying deal before the quarter does;
 *   • everything else → 'none' — silence is a valid, honest decision.
 *
 * Pure and IO-free: {@link decideChase} is a deterministic function of the
 * signal + config, {@link chaseToApprovalAction} wraps a move for the shared
 * approval inbox (never auto-sends), and {@link outcomeContractFor} ties the
 * chase deadline and margin floor to the deal's outcome contract so agents
 * chase toward a goal, not just activity.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Real engagement telemetry for one sent proposal. */
export interface ChaseSignal {
  /** Document lifecycle state, e.g. 'draft' | 'sent' | 'viewed' | 'signed' | 'declined'. */
  state: string;
  /** Total buyer-side views recorded. */
  viewCount: number;
  /** Epoch ms of the most recent view; null when never viewed. */
  lastViewedAt: number | null;
  /** Epoch ms the proposal went out; null when not yet sent. */
  sentAt: number | null;
  /** The section buyers dwell on most, e.g. 'terms'; null when unknown. */
  mostViewedSection: string | null;
  /** Injected clock (epoch ms) — deterministic in tests. */
  now: number;
}

export interface ChaseConfig {
  /** Hours of zero views after send before a proposal is flagged at risk. */
  staleHours: number;
  /** Views needed before an engaged-but-unsigned buyer earns a nudge. */
  viewThreshold: number;
}

/** A governed chase move — proposed, never executed, by this module. */
export type ChaseMove = {
  kind: 'nudge_email' | 'slack_ae' | 'revise_pricing' | 'flag_risk' | 'none';
  /** Plain-English why, shown on the approval card. */
  reason: string;
  /** Structured payload for the executor once approved. */
  payload: Record<string, unknown>;
};

/** Hot-but-unsigned proposals earn a nudge after this many hours. */
export const NUDGE_AFTER_HOURS = 48;

const HOUR_MS = 3_600_000;

/** States past which chasing is pointless (or rude). */
const CLOSED_STATES = new Set(['signed', 'declined', 'expired', 'closed']);

// ---------------------------------------------------------------------------
// decideChase
// ---------------------------------------------------------------------------

/**
 * Decide the single next chase move from real engagement telemetry.
 *
 *   • viewed ≥ cfg.viewThreshold AND unsigned AND sent > 48h ago →
 *     'nudge_email' citing the most-viewed section (the buyer is telling us
 *     what they're stuck on — the nudge speaks to exactly that);
 *   • sent AND viewCount == 0 AND sent > cfg.staleHours ago → 'flag_risk';
 *   • anything else (signed/declined, too fresh, mid-engagement) → 'none'.
 *
 * Pure and total: any signal produces a move; never throws.
 */
export function decideChase(sig: ChaseSignal, cfg: ChaseConfig): ChaseMove {
  // Closed proposals are history, not chase targets.
  if (CLOSED_STATES.has(sig.state)) {
    return { kind: 'none', reason: `Proposal is ${sig.state} — nothing to chase.`, payload: {} };
  }
  // Never sent → there is nothing out there to chase yet.
  if (sig.sentAt === null || sig.sentAt === undefined) {
    return { kind: 'none', reason: 'Proposal has not been sent yet.', payload: {} };
  }

  const hoursSinceSent = (sig.now - sig.sentAt) / HOUR_MS;

  // Hot but stalling: the buyer keeps reading and hasn't signed.
  if (sig.viewCount >= cfg.viewThreshold && hoursSinceSent > NUDGE_AFTER_HOURS) {
    const section = sig.mostViewedSection ?? 'the proposal';
    return {
      kind: 'nudge_email',
      reason:
        `Viewed ${sig.viewCount}× but unsigned ${Math.floor(hoursSinceSent)}h after send — ` +
        `buyer keeps returning to ${section}; nudge speaks to that section directly.`,
      payload: {
        mostViewedSection: section,
        viewCount: sig.viewCount,
        hoursSinceSent: Math.floor(hoursSinceSent),
        lastViewedAt: sig.lastViewedAt,
      },
    };
  }

  // Sent into a void: no views at all past the staleness window.
  if (sig.viewCount === 0 && hoursSinceSent > cfg.staleHours) {
    return {
      kind: 'flag_risk',
      reason:
        `Sent ${Math.floor(hoursSinceSent)}h ago and never viewed ` +
        `(threshold ${cfg.staleHours}h) — deal is going cold.`,
      payload: {
        hoursSinceSent: Math.floor(hoursSinceSent),
        staleHours: cfg.staleHours,
        viewCount: 0,
      },
    };
  }

  return {
    kind: 'none',
    reason: 'Engagement is within normal bounds — no chase warranted.',
    payload: {},
  };
}

// ---------------------------------------------------------------------------
// chaseToApprovalAction
// ---------------------------------------------------------------------------

export interface ChaseApprovalAction {
  type: `proposal.chase.${Exclude<ChaseMove['kind'], 'none'>}`;
  requiresApproval: true;
  /** What would change, for the confirm card. */
  diff: string;
  reason: string;
  payload: Record<string, unknown>;
}

/**
 * Wrap a chase move for the shared approval inbox. Every real move is
 * approval-gated — nothing auto-sends to a customer. 'none' returns null:
 * no move, no approval row, no noise.
 */
export function chaseToApprovalAction(move: ChaseMove): ChaseApprovalAction | null {
  if (move.kind === 'none') return null;
  return {
    type: `proposal.chase.${move.kind}`,
    requiresApproval: true,
    diff: `+ ${move.kind.replace(/_/g, ' ')} — ${move.reason}`,
    reason: move.reason,
    payload: { ...move.payload },
  };
}

// ---------------------------------------------------------------------------
// outcomeContractFor — deadlines tied to the contract, not to activity
// ---------------------------------------------------------------------------

export interface OutcomeContract {
  /** Plain-English goal the chase serves. */
  goal: string;
  /** Epoch ms by which the chase should have produced a signature or a call. */
  deadline: number;
  /** Minimum margin any revise_pricing move may propose — never crossed. */
  marginFloor: number;
}

export interface OutcomeContractCfg {
  /** Hours after send by which the deal must move (drives the deadline). */
  staleHours: number;
  /** 0..1 margin floor for pricing moves. */
  marginFloor: number;
}

/**
 * Derive the outcome contract governing chase moves for one deal: the goal,
 * the hard deadline (send time + cfg.staleHours; falls back to `now` when
 * unsent), and the margin floor a revise_pricing move may never go under.
 * Pure — deterministic for a given deal/cfg/now.
 */
export function outcomeContractFor(
  deal: { id?: string; name?: string; value?: number; sentAt?: number | null },
  cfg: OutcomeContractCfg,
  now: number = Date.now(),
): OutcomeContract {
  const anchor = deal.sentAt ?? now;
  const label = deal.name?.trim() || deal.id || 'this deal';
  const value =
    typeof deal.value === 'number' && deal.value > 0
      ? ` ($${deal.value.toLocaleString()})`
      : '';
  return {
    goal: `Get ${label}${value} signed or explicitly declined within ${cfg.staleHours}h of send.`,
    deadline: anchor + cfg.staleHours * HOUR_MS,
    marginFloor: cfg.marginFloor,
  };
}
